import React, { Suspense, lazy, useEffect, useState, useCallback } from "react";
import { ThemeProvider, StyledEngineProvider, CssBaseline } from "@mui/material";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import theme from "./theme";
import GlobalStyles from "./GlobalStyles";
import Pace from "./shared/components/Pace";
import { preloadPublicRouteForPath } from "./logged_out/components/publicRouteLoaders";
//import { createGlobalState } from 'react-hooks-global-state';
//import { createScreenScaler } from "screen-scaler";




const LoggedInComponent = lazy(() => import("./logged_in/components/Main"));

// A direct visit to a sign-in/booking/essay-share URL starts that route's
// chunk (the one that carries Firebase) alongside the public shell instead
// of after it, so moving those routes out of the landing bundle adds no
// round trip for them.
const LoggedOutComponent = lazy(() => {
  try { preloadPublicRouteForPath(window.location.pathname); } catch (e) { /* prefetch only */ }
  return import("./logged_out/components/Main");
});

const PublicAidWidget = lazy(() => import("./logged_out/components/PublicAidWidget"));

// The walkthrough registry (~115 KB of copy and animations) is only needed
// under /c, or when a #explainer=<name> preview hash is present. Load it
// with the logged-in shell rather than in the entry bundle every visitor
// downloads. Both explainers share the /c Suspense boundary with
// LoggedInComponent, so they are mounted (and listening for ts:page /
// ts:locked-tab) in the same commit as the shell, before any dashboard.
const loadExplainer = () => import("./shared/components/Explainer");
const PageAwareExplainer = lazy(() => loadExplainer().then((m) => ({ default: m.PageAwareExplainer })));
const LockedTabExplainer = lazy(() => loadExplainer().then((m) => ({ default: m.LockedTabExplainer })));
const LazyExplainerPreview = lazy(() => loadExplainer().then((m) => ({ default: m.ExplainerPreview })));

const EXPLAINER_PREVIEW_HASH = /explainer=[a-z0-9-]+/i;
const hasExplainerPreviewHash = () => {
  try {
    return EXPLAINER_PREVIEW_HASH.test(window.location.hash || '');
  } catch (e) {
    return false;
  }
};

// Mounts the hash-driven ExplainerPreview only once a #explainer=<name> hash
// has been seen; from then on it stays mounted and handles later hash
// changes itself, exactly as when it was always mounted.
export function ExplainerPreviewGate() {
  const [requested, setRequested] = useState(hasExplainerPreviewHash);
  useEffect(() => {
    if (requested) return undefined;
    const onHash = () => { if (hasExplainerPreviewHash()) setRequested(true); };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [requested]);
  if (!requested) return null;
  return (
    <Suspense fallback={null}>
      <LazyExplainerPreview />
    </Suspense>
  );
}

//const initialState = { count: 0 };
//const { useGlobalState } = createGlobalState(initialState);

//const [count, setCount] = useGlobalState('count');




function OfflineBanner() {
  const [offline, setOffline] = useState(!navigator.onLine);
  const [dismissed, setDismissed] = useState(false);
  const goOffline = useCallback(() => { setOffline(true); setDismissed(false); }, []);
  const goOnline = useCallback(() => { setOffline(false); setDismissed(false); }, []);

  useEffect(() => {
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, [goOffline, goOnline]);

  if (!offline || dismissed) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 99999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: 12, padding: '10px 20px',
      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 600,
      boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
    }}>
      <span style={{ fontSize: 18 }}>⚠️</span>
      <span>Can't connect to internet</span>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        style={{
          background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)',
          color: '#fff', borderRadius: 6, padding: '4px 14px', cursor: 'pointer',
          fontWeight: 700, fontSize: 13, marginLeft: 8,
        }}
      >
        Dismiss
      </button>
    </div>
  );
}

export function AppLoadingFallback() {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        color: '#1F3A63',
        background: '#F7FAFC',
        fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
        textAlign: 'center',
      }}
    >
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Loading TutorSpace…</div>
        <div style={{ fontSize: 14, opacity: 0.76 }}>Loading the files needed for this page.</div>
      </div>
    </div>
  );
}

function App() {
 
/*
  useEffect(() => {

    // Detect if the user agent is an iPad or iPhone
    const isIpadOrIphone =  true//  /iPad|iPhone/.test(navigator.userAgent) && !window.MSStream;
    // Directly apply scale to the body of the document
    const scaleValue = isIpadOrIphone ? '1.1' : '1';
   //document.body.style.transform = `scale(${scaleValue})`;
    document.body.style.transformOrigin = 'top left';
    document.body.style.width = isIpadOrIphone ? '150%' : '100%'; // Adjust width to compensate for the scale
  }, []);
*/





  return (
   
   
      <BrowserRouter>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <GlobalStyles />
            <OfflineBanner />
            <Pace color={theme.palette.primary.light} />
            <Suspense fallback={<AppLoadingFallback />}>
              <Switch>
                <Route path="/widget/aid">
                  <PublicAidWidget />
                </Route>
                <Route path="/c">
                  

                    <LoggedInComponent />
                    {/* PageAwareExplainer auto-fires student/family walkthroughs
                        for Dashboard (0), Tests (1), and Practice (8). Timed
                        tests use the runner's own on-demand Help guide.
                        Staff/reviewer views are excluded. */}
                    <PageAwareExplainer />
                    {/* LockedTabExplainer fires when a limited-plan user clicks a
                        nav tab they don't have access to. */}
                    <LockedTabExplainer />
                  
                </Route>
                <Route>
                  <LoggedOutComponent />
                </Route>
              </Switch>
            </Suspense>
            <ExplainerPreviewGate />
          </ThemeProvider>
        </StyledEngineProvider>
      </BrowserRouter>


  
  );
}

/*
<BrowserRouter>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <GlobalStyles />
          <Pace color={theme.palette.primary.light} />
          <Suspense fallback={<Fragment />}>
            <Switch>
              <Route path="/c">
                <Provider template={AlertMUITemplate} {...options}>
                  <LoggedInComponent />
                </Provider>
              </Route>
              <Route>
                <LoggedOutComponent />
              </Route>
            </Switch>
          </Suspense>
        </ThemeProvider>
      </StyledEngineProvider>
    </BrowserRouter>
*/
export default App;
